import { memo } from 'react';
import { Box, Text, theme, Tooltip } from '@frameio/vapor';
import { IconSequence } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/SequenceIcon';

TEXT.DEFINE({
  linkedToMultipleSequences: 'Linked to multiple sequences',
  linkedToSequence: 'Linked to sequence',
  linkedToSequenceWithName: (sequenceName: string) =>
    `Linked to ${sequenceName}`,
});

const TOOLTIP_MAX_WIDTH = 300;
const TOOLTIP_HORIZONTAL_PADDING = 24;
const TOOLTIP_TEXT_MAX_WIDTH = TOOLTIP_MAX_WIDTH - TOOLTIP_HORIZONTAL_PADDING;

export type SequenceIconProps = {
  /** Whether to hide the background of the badge */
  hideBackground?: boolean;
  /** When true, show "Linked to multiple sequences" in the tooltip */
  isLinkedToMultipleSequences?: boolean;
  /** The name of the Premiere sequence this asset is linked to */
  sequenceName?: string | null;
};

/**
 * Displays a sequence icon badge when an asset is linked to a Premiere sequence.
 */
export const SequenceIcon = memo(
  ({
    sequenceName,
    isLinkedToMultipleSequences,
    hideBackground,
  }: SequenceIconProps) => {
    let tooltipTitle = TEXT.linkedToSequence;

    if (isLinkedToMultipleSequences) {
      tooltipTitle = TEXT.linkedToMultipleSequences;
    } else if (sequenceName) {
      tooltipTitle = String(TEXT.linkedToSequenceWithName(sequenceName));
    }

    return (
      <Tooltip
        maxWidth={`${TOOLTIP_MAX_WIDTH}px`}
        placement="top"
        title={
          <Box maxWidth={`${TOOLTIP_TEXT_MAX_WIDTH}px`}>
            <Text
              color="primary"
              truncate={{ type: 'middle', padding: 10 }}
              variant="caption"
            >
              {tooltipTitle}
            </Text>
          </Box>
        }
      >
        <Box
          alignItems="center"
          aria-label={tooltipTitle}
          borderRadius={4}
          data-testid="sequence-icon"
          display="flex"
          gap={4}
          justifyContent="center"
          padding={1}
          role="img"
          style={{
            backgroundColor: hideBackground
              ? 'transparent'
              : theme.colors.components.badge.background.black,
          }}
        >
          <IconSequence
            aria-hidden={true}
            color={theme.colors.components.badge.icon}
            size={16}
          />
        </Box>
      </Tooltip>
    );
  }
);
